import {
    Dropdown,
    DropdownContent,
    DropdownTrigger,
    Button,
    DropdownItem,
} from "@jhool-io/fe-components";
import * as React from "react";
import { Link, NavLink } from "react-router-dom";
import SideNavLink from "./SideNavLink/SideNavLink";
import { useFetchUserDetails } from "../../hooks/queries/user";
import {
    cn,
    convertArrayToString,
    getTwoLetterAbbreviation,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../utils/helpers";
import { UserPermisions, UserRole } from "../../utils/types/user";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../hooks/permissions";
import patternBg from "../../assets/images/patternbg.png";
import PinIcon from "../Icons/Pin";
import { PinnedClientsContext } from "../../context/pinned-clients/PinnedClientsContext";
import {
    getFlaggedNoteListNavigateUrl,
    getNoteListNavigateUrl,
} from "../../modules/notes/helpers/notes.helpers";
import { getClaimsListNavigateUrl } from "../../modules/claims/helpers/claims.helper";
import { getRemitsListNavigateUrl } from "../../modules/remits/helpers/helpers.remits";
import { getBillingSummaryNavigateUrl } from "../../modules/billing/helpers/billing.helpers";
import useAuthContext from "../../hooks/useAuthContext";
import { useAppDispatch, useAppSelector } from "../../hooks/useRedux";
import { toggleNavState } from "../../redux/features/nav/navSlice";
import moment from "../../utils/moment";
import {
    getProvidersActivityNavigateUrl,
    getAgingNavigateUrl,
} from "../../modules/reports/helpers/reports.helper";
import { handleChangePractice } from "../../redux/features/practice/practiceSlice";
import CalendarIcon from "../../icons/Calendar";
import { useCanViewThirtyDaysProviders } from "../../hooks/useThirtydaysProviders";
import { setPracticeSession } from "../../utils/practiceSessionStore";
import useGetEPrescribeNotificationsCount from "../../modules/e-prescirbe/hooks/useGetEPrescribeNotificationsCount";
import useCanViewEPrescribeFlow from "../../modules/e-prescirbe/hooks/useCanViewEPrescribeFlow";

interface SideNavProps {
    userRoles?: string[];
}

export default function SideNav({ userRoles }: SideNavProps) {
    const { isOpen } = useAppSelector((state) => state.nav);

    const { practice } = useAppSelector((state) => state.userPractice);

    const canSeeAgingReportRoles = useDoesUserHaveRole([
        UserRole.SUPER_ADMIN,
        UserRole.CODER,
        UserRole.BILLER,
        UserRole.BILLER_ADMIN,
        UserRole.CLIENT_SUPPORT,
    ]);
    const canReadBillingReports = useDoesUserHavePermission([
        UserPermisions.BILLING_REPORTS_READ,
    ]);

    const canSeeAgingReport = canSeeAgingReportRoles || canReadBillingReports;

    const dispatch = useAppDispatch();

    const { data, isSuccess } = useFetchUserDetails();

    const pinnedClientsContext = React.useContext(PinnedClientsContext);

    const { logOutUser } = useAuthContext();

    const pinnedClients = pinnedClientsContext?.pinnedClients;

    const canAccessThirtyDaysProviders = useCanViewThirtyDaysProviders();
    const canViewEPrescribeFlow = useCanViewEPrescribeFlow();
    const ePrescribeNotifications = useGetEPrescribeNotificationsCount();

    const claimsListUrl = getClaimsListNavigateUrl();
    const remitsListUrl = getRemitsListNavigateUrl();
    const providersActivityUrl = getProvidersActivityNavigateUrl();
    const agingReportUrl = getAgingNavigateUrl();

    const billingSummaryUrl = getBillingSummaryNavigateUrl();

    const currentWeek = moment().startOf("week");
    const startOfWeek = currentWeek.startOf("week").format("YYYY-MM-DD");
    const endOfWeek = currentWeek.endOf("week").format("YYYY-MM-DD");

    // Function to display user initials in the right format
    const userInitials = (firstName: string, lastName: string) => {
        return `${firstName.charAt(0).toUpperCase()}${lastName
            .charAt(0)
            .toUpperCase()}`;
    };

    const isAllowedToView = (allowedRolesOrPermissions: string[]) => {
        return allowedRolesOrPermissions.some(
            (role) =>
                userRoles?.includes(role) ||
                practice?.roles?.includes(role as UserRole) ||
                practice?.permissions?.includes(role as UserPermisions)
        );
    };

    let isSmallerScreen = false;

    if (window) {
        isSmallerScreen =
            window && window.matchMedia("(max-width: 1000px)").matches;
    }

    const handleSideNavClick = () => {
        if (isSmallerScreen) {
            dispatch(toggleNavState());
        }
    };

    const billsUrl = "/billing-and-claims?invoice_status[]=open";

    // Temporarily hide some menu items for certain users
    const showMenu =
        data?.user_id !== "2ca707b2-1b94-4f03-9abc-444adda19a6e" &&
        data?.user_id !== "6d0a88be-35e0-4996-a9f6-ab79ceff8465";

    return (
        <>
            {isOpen && (
                <div
                    className="block md:hidden fixed w-full h-full bg-backdrop z-[11] overflow-hidden"
                    onClick={() => dispatch(toggleNavState())}
                    role="button"
                    tabIndex={0}
                    onKeyPress={() => dispatch(toggleNavState())}
                    aria-label="close sidebar"
                />
            )}
            <aside
                className={cn(
                    "bg-[--bg-sidebar] w-[--sidebar-width] h-dvh fixed left-0 top-0 flex flex-col z-[11] pb-[--padding-card-y-lg] transition-all ease-in-out duration-[0.25s] translate-x-0 py-2",
                    {
                        "-translate-x-full": !isOpen,
                    }
                )}
            >
                <div className="flex px-[--padding-card-x] h-10 items-center justify-between relative">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <Button
                                variant="can-hover"
                                className={cn(
                                    "[border:1px_solid_var(--color-stroke)] w-4/5"
                                )}
                            >
                                <span className="size-5 flex items-center justify-center rounded-md text-white text-small font-medium uppercase bg-primary shrink-0 mr-2">
                                    {getTwoLetterAbbreviation(
                                        practice?.practice_name || ""
                                    )}
                                </span>
                                <span className="text-caption font-medium mr-auto">
                                    {truncateString(
                                        practice?.practice_name || "",
                                        13
                                    )}
                                </span>
                                <svg
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="size-3 shrink-0 rotate-90 "
                                >
                                    <polyline points="16 18 22 12 16 6" />
                                    <polyline points="8 6 2 12 8 18" />
                                </svg>
                            </Button>
                        </DropdownTrigger>
                        <DropdownContent width="18.75rem">
                            {(data?.practices || []).map((userPractice) => (
                                <DropdownItem
                                    key={userPractice.practice_id}
                                    onClick={() => {
                                        if (
                                            userPractice.practice_id ===
                                            practice?.practice_id
                                        ) {
                                            return;
                                        }
                                        if (!data?.user_id) {
                                            return;
                                        }

                                        const nextPractice = {
                                            ...userPractice,
                                            user_id: data?.user_id,
                                        };

                                        setPracticeSession(nextPractice);

                                        dispatch(
                                            handleChangePractice({
                                                practice: nextPractice,
                                                updateLocation: true,
                                            })
                                        );
                                    }}
                                    isSelected={
                                        practice?.practice_id ===
                                        userPractice.practice_id
                                    }
                                >
                                    <div className="flex items-center">
                                        <span
                                            className={cn(
                                                "mr-2 flex items-center justify-center size-5 rounded-md bg-[#FFF6FB]"
                                            )}
                                        >
                                            <svg
                                                viewBox="0 0 24 24"
                                                width="24"
                                                height="24"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="shrink-0 size-3 stroke-[#0B132B]"
                                            >
                                                <rect
                                                    x="2"
                                                    y="2"
                                                    width="20"
                                                    height="20"
                                                    rx="2.18"
                                                    ry="2.18"
                                                />
                                                <line
                                                    x1="7"
                                                    y1="2"
                                                    x2="7"
                                                    y2="22"
                                                />
                                                <line
                                                    x1="17"
                                                    y1="2"
                                                    x2="17"
                                                    y2="22"
                                                />
                                                <line
                                                    x1="2"
                                                    y1="12"
                                                    x2="22"
                                                    y2="12"
                                                />
                                                <line
                                                    x1="2"
                                                    y1="7"
                                                    x2="7"
                                                    y2="7"
                                                />
                                                <line
                                                    x1="2"
                                                    y1="17"
                                                    x2="7"
                                                    y2="17"
                                                />
                                                <line
                                                    x1="7"
                                                    y1="17"
                                                    x2="12"
                                                    y2="17"
                                                />
                                                <line
                                                    x1="17"
                                                    y1="17"
                                                    x2="22"
                                                    y2="17"
                                                />
                                                <line
                                                    x1="17"
                                                    y1="7"
                                                    x2="22"
                                                    y2="7"
                                                />
                                            </svg>
                                        </span>
                                        <span>
                                            {userPractice.practice_name}
                                        </span>
                                    </div>
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                    <img
                        src={patternBg}
                        alt="pattern background"
                        className="absolute z-[-1] right-[62px] w-[77px] h-[65px]"
                    />
                    <Button
                        size="auto"
                        variant="can-hover"
                        onClick={() => dispatch(toggleNavState())}
                        className={cn(
                            "size-5 [border:1px_solid_var(--color-stroke)] hover:border-primary"
                        )}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="24"
                            height="24"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="css-i6dzq1 size-4 shrink-0"
                        >
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </Button>
                </div>

                <div className="flex-1 pb-[--padding-card-y-lg] relative overflow-x-hidden overflow-y-auto">
                    <div className="mb-[--spacing-header] overflow-x-hidden w-full px-[--padding-card-x] py-12">
                        <ul>
                            <SideNavLink
                                href="/"
                                title="Dashboard"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                        <polyline points="9 22 9 12 15 12 15 22" />
                                    </svg>
                                }
                                linkType="a"
                                isAllowedToView
                                onClick={handleSideNavClick}
                            />
                            <SideNavLink
                                href="/clients"
                                title="Clients"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                        <circle cx="9" cy="7" r="4" />
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    </svg>
                                }
                                linkType="a"
                                isAllowedToView={isAllowedToView([
                                    UserRole.SUPER_ADMIN,
                                    UserRole.PROVIDER,
                                    UserRole.BILLER,
                                    UserRole.BILLER_ADMIN,
                                    UserRole.AUDITOR,
                                    UserRole.CLIENT_SUPPORT,
                                    UserRole.SUPPORT_GENERAL,
                                    UserRole.SUPPORT_ADMIN,
                                ])}
                                onClick={handleSideNavClick}
                            />

                            <SideNavLink
                                href={`/my-schedule?from_date=${startOfWeek}&to_date=${endOfWeek}`}
                                title="Schedule"
                                icon={<CalendarIcon className="size-3.5" />}
                                linkType="a"
                                isAllowedToView={isAllowedToView([
                                    UserRole.PROVIDER,
                                    UserRole.BILLER,
                                    UserRole.BILLER_ADMIN,
                                    UserRole.SUPER_ADMIN,
                                ])}
                                onClick={handleSideNavClick}
                            />

                            <SideNavLink
                                title="Session Notes"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                                    </svg>
                                }
                                linkType="button"
                                isAllowedToView={isAllowedToView([
                                    UserRole.SUPER_ADMIN,
                                    UserRole.PROVIDER,
                                    UserRole.BILLER,
                                    UserRole.BILLER_ADMIN,
                                    UserRole.CODER,
                                    UserRole.AUDITOR,
                                    UserRole.SUPPORT_GENERAL,
                                ])}
                                dropdown={
                                    <>
                                        <NavLink
                                            end
                                            to={getNoteListNavigateUrl(
                                                practice?.roles || []
                                            )}
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return isActive
                                                    ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                    : undefined;
                                            }}
                                        >
                                            Notes
                                        </NavLink>
                                        {isAllowedToView([
                                            UserRole.AUDITOR,
                                            UserRole.BILLER,
                                            UserRole.BILLER_ADMIN,
                                            UserRole.CODER,
                                            UserRole.SUPER_ADMIN,
                                            UserRole.AUDITOR_ADMIN,
                                            UserRole.SUPPORT_GENERAL,
                                        ]) ? (
                                            <NavLink
                                                end
                                                to={getFlaggedNoteListNavigateUrl()}
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Flagged notes
                                            </NavLink>
                                        ) : null}
                                        {isAllowedToView([
                                            UserRole.AUDITOR,
                                            UserRole.AUDITOR_ADMIN,
                                            UserRole.SUPER_ADMIN,
                                            UserRole.SUPPORT_GENERAL,
                                        ]) ? (
                                            <NavLink
                                                end
                                                to="/notes/note-audit-dashboard?date=this_month"
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Note audit
                                            </NavLink>
                                        ) : null}
                                        {isAllowedToView([
                                            UserRole.BILLER,
                                            UserRole.BILLER_ADMIN,
                                            UserRole.CODER,
                                            UserRole.SUPER_ADMIN,
                                            UserRole.SUPPORT_GENERAL,
                                        ]) ? (
                                            <NavLink
                                                end
                                                to="/notes/coded-client-notes"
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Coded notes
                                            </NavLink>
                                        ) : null}
                                        {isAllowedToView([
                                            UserRole.BILLER,
                                            UserRole.BILLER_ADMIN,
                                            UserRole.CODER,
                                            UserRole.SUPER_ADMIN,
                                            UserRole.SUPPORT_GENERAL,
                                        ]) ? (
                                            <NavLink
                                                end
                                                to="/notes/pending-code-review"
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Pending code review
                                            </NavLink>
                                        ) : null}
                                    </>
                                }
                            />
                            <SideNavLink
                                title="ePrescribe"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <line x1="9" y1="15" x2="15" y2="15" />
                                    </svg>
                                }
                                linkType="button"
                                isAllowedToView={canViewEPrescribeFlow}
                                dropdown={
                                    <>
                                        <NavLink
                                            end
                                            to="/prescriptions?status=pending"
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return cn("flex items-center", {
                                                    "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active] ":
                                                        isActive,
                                                });
                                            }}
                                        >
                                            Prescriptions
                                            {
                                                ePrescribeNotifications?.pendingPrescriptions
                                            }
                                        </NavLink>
                                        <NavLink
                                            end
                                            to="/pharmacy-messages"
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return cn("flex items-center", {
                                                    "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active] ":
                                                        isActive,
                                                });
                                            }}
                                        >
                                            Pharmacy Messages
                                            {
                                                ePrescribeNotifications?.therapyMessages
                                            }
                                        </NavLink>
                                        <NavLink
                                            end
                                            to="/lab-requests"
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return cn("flex items-center", {
                                                    "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active] ":
                                                        isActive,
                                                });
                                            }}
                                        >
                                            Lab Requests
                                        </NavLink>
                                    </>
                                }
                            />
                            <SideNavLink
                                title="Billings"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <rect
                                            x="1"
                                            y="4"
                                            width="22"
                                            height="16"
                                            rx="2"
                                            ry="2"
                                        />
                                        <line x1="1" y1="10" x2="23" y2="10" />
                                    </svg>
                                }
                                isAllowedToView={
                                    showMenu &&
                                    isAllowedToView([
                                        UserRole.SUPER_ADMIN,
                                        UserRole.BILLER,
                                        UserRole.BILLER_ADMIN,
                                        UserRole.CODER,
                                        UserRole.CLIENT_SUPPORT,
                                        UserRole.SUPPORT_GENERAL,
                                    ])
                                }
                                linkType="button"
                                dropdown={
                                    <>
                                        <NavLink
                                            end
                                            to={billsUrl}
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return isActive
                                                    ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                    : undefined;
                                            }}
                                            hidden={
                                                !isAllowedToView([
                                                    UserPermisions.CLIENT_INFO_INVOICE_READ,
                                                ])
                                            }
                                        >
                                            Bills
                                        </NavLink>
                                        <NavLink
                                            end
                                            to="/billing-payments?invoice_status%5B%5D=open"
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return isActive
                                                    ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                    : undefined;
                                            }}
                                            hidden={
                                                !isAllowedToView([
                                                    UserPermisions.CLIENT_INFO_ALL_ADMIN,
                                                    UserPermisions.CLIENT_INFO_PAYMENTS_READ,
                                                ])
                                            }
                                        >
                                            Payments
                                        </NavLink>
                                    </>
                                }
                            />
                            <SideNavLink
                                linkType="button"
                                title="Claims"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />
                                    </svg>
                                }
                                isAllowedToView={
                                    showMenu &&
                                    isAllowedToView([
                                        UserRole.SUPER_ADMIN,
                                        UserRole.BILLER,
                                        UserRole.BILLER_ADMIN,
                                        UserRole.CODER,
                                        UserRole.CLIENT_SUPPORT,
                                        UserRole.SUPPORT_GENERAL,
                                    ])
                                }
                                dropdown={
                                    <>
                                        <NavLink
                                            end
                                            to={claimsListUrl}
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return isActive
                                                    ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                    : undefined;
                                            }}
                                        >
                                            Claims
                                        </NavLink>

                                        <NavLink
                                            end
                                            to={remitsListUrl}
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return isActive
                                                    ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                    : undefined;
                                            }}
                                        >
                                            Remits
                                        </NavLink>
                                    </>
                                }
                            />

                            <SideNavLink
                                href="/tasks?status[]=not_started&status[]=in_progress&category[]=user_created_todo"
                                title="Tasks"
                                linkType="a"
                                isAllowedToView={isAllowedToView([
                                    UserRole.SUPER_ADMIN,
                                    UserRole.PROVIDER,
                                    UserRole.BILLER,
                                    UserRole.BILLER_ADMIN,
                                    UserRole.CODER,
                                    UserRole.AUDITOR,
                                    UserRole.SUPPORT_GENERAL,
                                ])}
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <line x1="8" y1="6" x2="21" y2="6" />
                                        <line x1="8" y1="12" x2="21" y2="12" />
                                        <line x1="8" y1="18" x2="21" y2="18" />
                                        <line x1="3" y1="6" x2="3.01" y2="6" />
                                        <line
                                            x1="3"
                                            y1="12"
                                            x2="3.01"
                                            y2="12"
                                        />
                                        <line
                                            x1="3"
                                            y1="18"
                                            x2="3.01"
                                            y2="18"
                                        />
                                    </svg>
                                }
                                onClick={handleSideNavClick}
                            />
                            <SideNavLink
                                href="/providers"
                                title="Providers"
                                linkType="a"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                        <circle cx="9" cy="7" r="4" />
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    </svg>
                                }
                                isAllowedToView={Boolean(
                                    practice?.permissions?.includes(
                                        UserPermisions.PROVIDER_INFO_READ
                                    )
                                )}
                                onClick={handleSideNavClick}
                            />
                            <SideNavLink
                                linkType="button"
                                title="Client Forms"
                                dropdown={
                                    <>
                                        <NavLink
                                            end
                                            to="/client-forms/consent-form-reports"
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return isActive
                                                    ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                    : undefined;
                                            }}
                                        >
                                            Consent Form Reports
                                        </NavLink>

                                        <NavLink
                                            end
                                            to="/client-forms/other-forms"
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return isActive
                                                    ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                    : undefined;
                                            }}
                                        >
                                            Other forms
                                        </NavLink>
                                    </>
                                }
                                icon={
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <polyline points="21 8 21 21 3 21 3 8" />
                                        <rect
                                            x="1"
                                            y="3"
                                            width="22"
                                            height="5"
                                        />
                                        <line x1="10" y1="12" x2="14" y2="12" />
                                    </svg>
                                }
                                isAllowedToView={isAllowedToView([
                                    UserRole.SUPER_ADMIN,
                                    UserRole.CLIENT_SUPPORT,
                                    UserRole.SUPPORT_GENERAL,
                                ])}
                            />
                            <SideNavLink
                                linkType="button"
                                title="Reports"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <line x1="16" y1="13" x2="8" y2="13" />
                                        <line x1="16" y1="17" x2="8" y2="17" />
                                        <polyline points="10 9 9 9 8 9" />
                                    </svg>
                                }
                                isAllowedToView={
                                    isAllowedToView([
                                        UserPermisions.PROVIDER_REPORTS_READ,
                                    ]) && showMenu
                                }
                                dropdown={
                                    <>
                                        {canSeeAgingReport ? (
                                            <NavLink
                                                end
                                                to={agingReportUrl}
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Aging Reports
                                            </NavLink>
                                        ) : null}

                                        <NavLink
                                            end
                                            to="/reports/auditing-reports"
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            hidden={
                                                !isAllowedToView([
                                                    UserPermisions.PROVIDER_REPORTS_READ,
                                                    UserPermisions.CLIENT_INFO_ALL_ADMIN,
                                                ])
                                            }
                                            className={({ isActive }) => {
                                                return isActive
                                                    ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                    : undefined;
                                            }}
                                        >
                                            Auditing Reports
                                        </NavLink>

                                        {!practice?.roles?.includes(
                                            UserRole.AUDITOR_ADMIN
                                        ) ? (
                                            <NavLink
                                                end
                                                to="/reports/billing-reports"
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Billing reports
                                            </NavLink>
                                        ) : null}
                                        {isAllowedToView([
                                            UserPermisions.BILLING_DASHBOARD_READ,
                                        ]) ? (
                                            <NavLink
                                                end
                                                to={billingSummaryUrl}
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                                hidden
                                            >
                                                Billing Summary
                                            </NavLink>
                                        ) : null}

                                        {isAllowedToView([
                                            UserPermisions.USER_INFO_ALL_WRITE,
                                            UserRole.SUPPORT_GENERAL,
                                        ]) ? (
                                            <NavLink
                                                end
                                                to="/reports/confirmation-reports"
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Confirmation Reports
                                            </NavLink>
                                        ) : null}
                                        <NavLink
                                            end
                                            to={providersActivityUrl}
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return isActive
                                                    ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                    : undefined;
                                            }}
                                        >
                                            Provider session activity
                                        </NavLink>
                                        <NavLink
                                            end
                                            to="/reports/terminated-clients"
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return isActive
                                                    ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                    : undefined;
                                            }}
                                        >
                                            Terminated Clients
                                        </NavLink>
                                    </>
                                }
                            />
                            <SideNavLink
                                linkType="a"
                                href="/practices"
                                title="Practices"
                                isAllowedToView={
                                    showMenu &&
                                    isAllowedToView([
                                        UserRole.SUPER_ADMIN,
                                        UserRole.SUPPORT_GENERAL,
                                    ])
                                }
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                                    </svg>
                                }
                            />

                            <SideNavLink
                                linkType="button"
                                title="Practice Administration"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <circle cx="12" cy="12" r="3" />
                                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                                    </svg>
                                }
                                isAllowedToView={
                                    showMenu &&
                                    isAllowedToView([
                                        UserRole.AUDITOR,
                                        UserRole.BILLER,
                                        UserRole.BILLER_ADMIN,
                                        UserRole.CLIENT_SUPPORT,
                                        UserRole.CODER,
                                        UserRole.SUPER_ADMIN,
                                        UserRole.SUPPORT_GENERAL,
                                        UserRole.SUPPORT_ADMIN,
                                    ])
                                }
                                dropdown={
                                    <>
                                        {isAllowedToView([
                                            UserRole.SUPER_ADMIN,
                                            UserRole.BILLER_ADMIN,
                                            UserRole.SUPPORT_ADMIN,
                                            UserRole.SUPPORT_GENERAL,
                                        ]) && (
                                            <NavLink
                                                end
                                                to="/user-management?status=active"
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                User management
                                            </NavLink>
                                        )}

                                        {!practice?.provider_id && (
                                            <NavLink
                                                end
                                                to="/audit-log"
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Audit Log
                                            </NavLink>
                                        )}

                                        {isAllowedToView([
                                            UserPermisions.PRACTICE_SETTING_ADMIN,
                                            UserPermisions.PRACTICE_SETTING_WRITE,
                                            UserPermisions.PRACTICE_SETTING_READ,
                                        ]) && (
                                            <NavLink
                                                end
                                                to="/practice-settings"
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Practice Settings
                                            </NavLink>
                                        )}

                                        {isAllowedToView([
                                            UserRole.SUPER_ADMIN,
                                            UserRole.BILLER_ADMIN,
                                            UserRole.SUPPORT_GENERAL,
                                        ]) && (
                                            <NavLink
                                                end
                                                to="/client-chart-restrictions"
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Client chart restrictions
                                            </NavLink>
                                        )}

                                        {isAllowedToView([
                                            UserRole.SUPER_ADMIN,
                                            UserRole.BILLER_ADMIN,
                                            UserRole.CODER,
                                            UserRole.BILLER,
                                            UserRole.SUPPORT_GENERAL,
                                        ]) && (
                                            <NavLink
                                                end
                                                to="/payers"
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Payers
                                            </NavLink>
                                        )}
                                        {isAllowedToView([
                                            UserRole.SUPER_ADMIN,
                                            UserRole.BILLER_ADMIN,
                                            UserRole.CODER,
                                            UserRole.BILLER,
                                            UserRole.SUPPORT_GENERAL,
                                        ]) && (
                                            <NavLink
                                                end
                                                to="/remark-codes"
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Remark Codes
                                            </NavLink>
                                        )}
                                        {isAllowedToView([
                                            UserRole.SUPER_ADMIN,
                                            UserRole.SUPPORT_GENERAL,
                                        ]) && (
                                            <NavLink
                                                end
                                                to="/announcements"
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Announcements
                                            </NavLink>
                                        )}
                                        {canAccessThirtyDaysProviders && (
                                            <NavLink
                                                end
                                                to="/thirty-days-providers"
                                                onClick={
                                                    isSmallerScreen
                                                        ? () =>
                                                              dispatch(
                                                                  toggleNavState()
                                                              )
                                                        : undefined
                                                }
                                                className={({ isActive }) => {
                                                    return isActive
                                                        ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                        : undefined;
                                                }}
                                            >
                                                Thirty days Providers
                                            </NavLink>
                                        )}

                                        <NavLink
                                            end
                                            to="/note-access-overrides"
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return isActive
                                                    ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                    : undefined;
                                            }}
                                            hidden={
                                                !isAllowedToView([
                                                    UserPermisions.PROVIDER_STATUS_RULE_READ,
                                                ])
                                            }
                                        >
                                            Note Access Overrides
                                        </NavLink>
                                        <NavLink
                                            end
                                            to="/note-rule-bypasses"
                                            onClick={
                                                isSmallerScreen
                                                    ? () =>
                                                          dispatch(
                                                              toggleNavState()
                                                          )
                                                    : undefined
                                            }
                                            className={({ isActive }) => {
                                                return isActive
                                                    ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                    : undefined;
                                            }}
                                            hidden={
                                                !isAllowedToView([
                                                    UserPermisions.NOTE_RULE_BYPASS_READ,
                                                ])
                                            }
                                        >
                                            Note Rule Overrides
                                        </NavLink>
                                    </>
                                }
                            />

                            <SideNavLink
                                linkType="button"
                                title="Tools"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                                    </svg>
                                }
                                isAllowedToView={
                                    showMenu &&
                                    isAllowedToView([
                                        UserRole.SUPER_ADMIN,
                                        UserRole.BILLER,
                                        UserRole.CODER,
                                        UserRole.BILLER_ADMIN,
                                        UserRole.CLIENT_SUPPORT,
                                        UserRole.CLIENT,
                                        UserRole.SUPPORT_GENERAL,
                                    ])
                                }
                                dropdown={
                                    <NavLink
                                        end
                                        to="/tools/fees-calculator"
                                        onClick={
                                            isSmallerScreen
                                                ? () =>
                                                      dispatch(toggleNavState())
                                                : undefined
                                        }
                                        className={({ isActive }) => {
                                            return isActive
                                                ? "text-[--sidebar-text-dropdown-active] before:!bg-[--sidebar-text-dropdown-active]"
                                                : undefined;
                                        }}
                                    >
                                        Fees Calculator
                                    </NavLink>
                                }
                            />

                            <SideNavLink
                                href="/user-preferences"
                                title="Preferences"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <circle cx="12" cy="12" r="3" />
                                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                                    </svg>
                                }
                                linkType="a"
                                isAllowedToView
                                onClick={handleSideNavClick}
                            />
                        </ul>
                    </div>
                    {pinnedClients &&
                        pinnedClients.length > 0 &&
                        pinnedClients.length <= 5 && (
                            <div className="px-[--padding-card-x] py-4">
                                <h4 className="pl-6 uppercase text-textSec text-base font-medium mb-[--spacing-items]">
                                    Pinned clients
                                </h4>
                                <div className="flex flex-col gap-y-1">
                                    {pinnedClients.map((client) => (
                                        <Link
                                            key={`${client.client_id} ${client.pin_module}`}
                                            to={client.pin_location}
                                            className="group link capitalize relative !h-11 [&_svg_path_fill]:text-text"
                                        >
                                            <PinIcon className="mr-2" />
                                            <span className="flex items-start justify-center flex-col gap-y-1 leading-none">
                                                <span className="">
                                                    {truncateString(
                                                        client.client_name,
                                                        16
                                                    )}
                                                </span>
                                                <span className="text-small font-medium leading-none normal-case">
                                                    {makeStringFirstLetterCapital(
                                                        removeEnumUnderscore(
                                                            client.pin_module
                                                        )
                                                    )}
                                                </span>
                                            </span>

                                            <Button
                                                className="hidden group-hover:flex ml-auto"
                                                size="auto"
                                                variant="can-hover"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    pinnedClientsContext.handleUnpinClient(
                                                        client.client_id,
                                                        client.pin_module
                                                    );
                                                }}
                                            >
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    width="24"
                                                    height="24"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="size-3.5 shrink-0 stroke-textSec"
                                                >
                                                    <line
                                                        x1="18"
                                                        y1="6"
                                                        x2="6"
                                                        y2="18"
                                                    />
                                                    <line
                                                        x1="6"
                                                        y1="6"
                                                        x2="18"
                                                        y2="18"
                                                    />
                                                </svg>
                                            </Button>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                    <div className="mt-[--spacing-items] block md:hidden">
                        {data && isSuccess && (
                            <div
                                className={`px-[--padding-card-x] before:block before:content-[''] before:mb-[--spacing-items] before:h-px before:w-full`}
                            >
                                <div className="flex items-center gap-x-2">
                                    <div className="size-6 rounded-full [border:2px_solid_var(--border-theme-switch)] flex items-center justify-center text-small text-text font-semibold shadow-sm">
                                        {userInitials(
                                            removeEnumUnderscore(
                                                data.first_name
                                            ),
                                            removeEnumUnderscore(data.last_name)
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-y-0.5">
                                        <span className="font-medium text-base">
                                            {`${data.first_name} ${data.last_name}`}
                                        </span>
                                        <span className="font-normal  text-textSec leading-[16px] capitalize text-caption">
                                            {convertArrayToString(
                                                practice?.roles || [],
                                                ","
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className="mt-7">
                                    <Button
                                        className="text-danger cursor-pointer"
                                        size="auto"
                                        variant="normal"
                                        type="button"
                                        onClick={logOutUser}
                                        iconLeft={
                                            <svg
                                                viewBox="0 0 24 24"
                                                width="24"
                                                height="24"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="size-4"
                                            >
                                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                                <polyline points="16 17 21 12 16 7" />
                                                <line
                                                    x1="21"
                                                    y1="12"
                                                    x2="9"
                                                    y2="12"
                                                />
                                            </svg>
                                        }
                                    >
                                        Logout
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                    <img
                        src={patternBg}
                        alt="pattern background"
                        className="absolute bottom-0 left-0 right-[62px] w-[194px] h-[165px] -z-[1]"
                    />
                </div>
            </aside>
        </>
    );
}
